// Single source of truth for a procedure's optional scheduled "active window"
// — the date range during which the procedure is VISIBLE to patients. Imported
// by the server (Ashley's active-specials context) and the client funnel
// (QuoteProvider catalog filter) via the `@shared` alias.
//
// SCOPE — VISIBILITY. Outside its window a procedure is hidden from the
// patient-facing procedure list entirely (the funnel filters it out, the same
// way isInternal/isDiscontinued procedures are excluded). The window is
// procedure-agnostic: it applies to any procedure, not just SPECIAL ones.
// Pricing is unaffected — special pricing still keys off the raw SPECIAL flag.

// A bound can arrive as a Date (server `toObject()`), an epoch-ms number (the
// GraphQL `Date` scalar serializes to ms — see resolvers.ts), or an ISO
// string. Coerce all three to epoch ms; return null for absent/invalid.
function toMs(value: Date | number | string | null | undefined): number | null {
  if (value == null) return null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export interface ActiveWindowInput {
  activeStartsAt?: Date | number | string | null;
  activeEndsAt?: Date | number | string | null;
}

/**
 * Whether a procedure is inside its scheduled active window right now (i.e.
 * should be VISIBLE to patients).
 *
 * Each bound is optional, so:
 *   - no bounds        → always visible (preserves pre-window behavior; every
 *                        procedure without a window is unchanged)
 *   - only `startsAt`  → visible from that instant onward
 *   - only `endsAt`    → visible until that instant
 * Bounds are inclusive. A malformed/empty window degrades to "visible",
 * matching the no-window case (fail-open: we'd rather show a procedure than
 * silently hide it on a bad date).
 */
export function isWithinActiveWindow(
  procedure: ActiveWindowInput | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!procedure) return true;
  const startMs = toMs(procedure.activeStartsAt);
  const endMs = toMs(procedure.activeEndsAt);
  if (startMs != null && now < startMs) return false;
  if (endMs != null && now > endMs) return false;
  return true;
}
